import {
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import toast from 'react-hot-toast';

const {Text} = Typography;
const PRIMARY = '#4f46e5';
const PRIMARY_LIGHT = '#eef2ff';

const SectionTitle = ({children}) => (
  <div className={'flex items-center gap-2 mb-4'}>
    <div className={'w-1 h-5 rounded-full'} style={{background: PRIMARY}} />
    <Text className={'font-bold text-gray-700 text-sm uppercase tracking-widest'}>{children}</Text>
  </div>
);
SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

const EditProductScreen = ({
  editModal,
  setEditModal,
  form,
  updateProductMutation,
  token,
  categoryOptions,
  brandOptions,
}) => {

  const handleSubmit = async values => {
    try {
      const payload = {
        productName: values.productName,
        status: values.status || 'ACTIVE',
        eanCode: values.eanCode,
        quantity: values.quantity ?? 0,

        cgst: Number(values.cgst || 0),
        sgst: Number(values.sgst || 0),
        igst: Number(values.igst || 0),
        tax: {
          cgst: Number(values.cgst || 0),
          sgst: Number(values.sgst || 0),
          igst: Number(values.igst || 0),
        },
      };
      await updateProductMutation.mutateAsync({
        token,
        payload,
        id: editModal.record.id,
      });

      toast.success('Product updated successfully');
      form.resetFields();
      setEditModal({visible: false, record: null});
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Update failed');
    }
  };

  // Watch cgst and sgst values dynamically to auto-update igst
  const cgstValue = Form.useWatch('cgst', form);
  const sgstValue = Form.useWatch('sgst', form);

  // Automatically update igst when cgst or sgst changes
  useEffect(() => {
    const sum = Number((Number(cgstValue || 0) + Number(sgstValue || 0)).toFixed(2));
    form.setFieldsValue({igst: sum});
  }, [cgstValue, sgstValue, form]);
  
  useEffect(() => {
    if (editModal.visible && editModal.record) {
      form.setFieldsValue({
        productName: editModal.record.productName,
        status: editModal.record.status,
        eanCode: editModal.record.eanCode,
        quantity: editModal.record.quantity,

        cgst: editModal.record.tax?.cgst ?? 0,
        sgst: editModal.record.tax?.sgst ?? 0,
        igst: editModal.record.tax?.igst ?? 0,
      });
    }
  }, [editModal.visible, editModal.record, form]);
  const handleClose = () => {
    setEditModal({visible: false, record: null});
    form.resetFields();
  };

  return (
    <Modal
      title={(
        <div className={'flex items-center gap-3 py-1'}>
          <div
            className={'w-8 h-8 rounded-xl flex items-center justify-center text-white text-sm font-bold'}
            style={{background: 'linear-gradient(135deg, #f59e0b, #ef4444)'}}
          >
            ✏
          </div>
          <div>
            <div className={'font-bold text-gray-800 text-base'}>Edit Product</div>
            <div className={'text-gray-400 font-normal'}>{editModal.record?.productName}</div>
          </div>
        </div>
      )}
      open={editModal.visible}
      width={860}
      onCancel={handleClose}
      onOk={() => form.submit()}
      okText={'Save Changes'}
      okButtonProps={{
        className: 'btn-primary px-6',
        loading: updateProductMutation?.isPending || updateProductMutation?.isLoading,
      }}
      cancelButtonProps={{className: 'btn-secondary'}}
      destroyOnHidden
      styles={{
        header: {borderBottom: `3px solid ${PRIMARY_LIGHT}`, paddingBottom: 16},
        body: {padding: '20px 24px', maxHeight: '75vh', overflowY: 'auto'},
      }}
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleSubmit}
        initialValues={{
          status: 'ACTIVE',
          quantity: 0,
          cgst: 0,
          sgst: 0,
        }}
      >
        {/* BASIC INFO */}
        <div className={'rounded-md p-4 bg-primary-50 mb-4 border border-primary-100'}>
          <SectionTitle>Basic Information</SectionTitle>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name={'productName'}
                label={<span className={'font-semibold text-gray-600'}>Product Name</span>}
                rules={[{required: true, message: 'Product name is required'}]}
              >
                <Input className={'rounded-md'} placeholder={'Product name'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'eanCode'}
                label={<span className={'font-semibold text-gray-600'}>EAN Code</span>}
                rules={[{required: true, message: 'EAN Code is required'}]}
              >
                <Input className={'rounded-md'} maxLength={13} placeholder={'13-digit EAN'} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'status'}
                label={<span className={'font-semibold text-gray-600'}>Status</span>}
                rules={[{required: true}]}
              >
                <Select
                  options={[
                    {label: '✅ Active', value: 'ACTIVE'},
                    {label: '❌ Inactive', value: 'INACTIVE'},
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name={'quantity'}
                label={<span className={'font-semibold text-gray-600'}>Quantity</span>}
                rules={[
                  {required: true, message: 'Quantity is required'},
                  {type: 'number', min: 0, message: 'Must be a positive number'},
                ]}
              >
                <InputNumber min={0} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>
           
          </Row>
         
        </div>

        {/* TAX */}
        <div className={'rounded-md p-4 bg-white border border-primary-100'}>
          <SectionTitle>Tax Configuration</SectionTitle>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name={'cgst'}
                label={<span className={'font-semibold text-gray-600'}>CGST (%)</span>}
              >
                <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={'sgst'}
                label={<span className={'font-semibold text-gray-600'}>SGST (%)</span>}
              >
                <InputNumber min={0} max={100} step={0.01} className={'w-full rounded-md'} placeholder={'0'} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={'igst'}
                label={<span className={'font-semibold'}>IGST (%)</span>}
              >
                <InputNumber 
                  disabled
                  min={0} 
                  max={100} 
                  step={0.01} 
                  className={'w-full rounded-md'} 
                  placeholder={'0'} 
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

      </Form>
    </Modal>
  );
};

EditProductScreen.propTypes = {
  editModal: PropTypes.object.isRequired,
  setEditModal: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  updateProductMutation: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  brandOptions: PropTypes.array.isRequired,
};

export default EditProductScreen;
